import { Copy, Hash, Linkedin, Mail, MessageCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { copy } from '../copy/investor-copy'

export const LINKEDIN_APP = 'https://www.linkedin.com/feed/'
export const SLACK_APP = 'https://app.slack.com/'

/** Slack and LinkedIn take no pre-filled text: the message is copied, then the app opens (handoff 04). */
export async function copyThenOpen(text: string, url: string) {
  await navigator.clipboard.writeText(text)
  window.open(url, '_blank', 'noopener')
}

/** A send tile: the icon over its label, as the prototype's "Send with" grid draws them. */
const SEND_TILE =
  'flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-lg border border-border bg-input px-2 py-2.5 text-xs text-foreground transition-colors hover:bg-hover'

/**
 * The "Send with" grid of the share and invite windows: copy the message, or send it by email, WhatsApp, Telegram,
 * LinkedIn or Slack. `text` is what is sent, the link included.
 */
export function SendWithTiles({ text, subject }: { text: string; subject: string }) {
  const [copied, setCopied] = useState<'message' | 'email' | null>(null)
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(text)}`
  const telegram = `https://t.me/share/url?text=${encodeURIComponent(text)}`
  const copyMessage = async (from: 'message' | 'email') => {
    await navigator.clipboard.writeText(text)
    setCopied(from)
  }
  return (
    <>
      <p className="mt-5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {copy('share.sendWith')}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <button type="button" onClick={() => copyMessage('message')} className={SEND_TILE}>
          <Copy className="size-4" aria-hidden />
          <span>{copied === 'message' ? copy('share.copied') : copy('share.copyMessage')}</span>
        </button>
        {/* The email app may not open (no mail app set up, or a browser with no mail site): the message is copied as
            well, so it can be pasted. */}
        <a href={mailto} onClick={() => copyMessage('email')} className={SEND_TILE}>
          <Mail className="size-4" aria-hidden />
          <span>{copied === 'email' ? copy('share.copied') : copy('share.email')}</span>
        </a>
        <a href={whatsapp} target="_blank" rel="noreferrer" className={SEND_TILE}>
          <MessageCircle className="size-4" aria-hidden />
          <span>{copy('share.whatsapp')}</span>
        </a>
        <a href={telegram} target="_blank" rel="noreferrer" className={SEND_TILE}>
          <Send className="size-4" aria-hidden />
          <span>{copy('share.telegram')}</span>
        </a>
        <button
          type="button"
          onClick={() => copyThenOpen(text, LINKEDIN_APP)}
          className={SEND_TILE}
        >
          <Linkedin className="size-4" aria-hidden />
          <span>{copy('share.linkedin')}</span>
        </button>
        <button type="button" onClick={() => copyThenOpen(text, SLACK_APP)} className={SEND_TILE}>
          <Hash className="size-4" aria-hidden />
          <span>{copy('share.slack')}</span>
        </button>
      </div>
    </>
  )
}
